// Port de firstPlayableStream : on essaie les lecteurs d'un épisode dans
// l'ordre et on retourne le premier flux réellement exploitable. Un lecteur
// hors ligne, un embed supprimé ou un master illisible font passer au suivant.

import { parseManifest } from "./hls.ts";
import type { Manifest } from "./hls.ts";
import { fetchText, proxyUrl } from "./proxy.ts";
import { embedLabel, resolvePlayables } from "./resolvers.ts";
import type { Playable } from "./resolvers.ts";

export interface Stream {
  /** URL directement lisible par le navigateur : elle passe par le proxy. */
  url: string;
  /** URL amont, utile au diagnostic. */
  source: string;
  referer: string;
  isHLS: boolean;
  /** Nom du lecteur d'où vient le flux. */
  player: string;
  /** Manifeste analysé, pour le sélecteur de qualité et le téléchargement. */
  manifest: Manifest | null;
}

export interface ResolveProgress {
  (message: string): void;
}

async function toStream(
  playable: Playable,
  player: string,
): Promise<Stream | null> {
  const url = proxyUrl(playable.url, playable.referer);

  // fichier progressif : rien à décliner, il est jouable tel quel
  if (!playable.isHLS) {
    return {
      url,
      source: playable.url,
      referer: playable.referer,
      isHLS: false,
      player,
      manifest: null,
    };
  }

  const body = await fetchText(playable.url, { referer: playable.referer });
  if (body === "") return null;

  const manifest = parseManifest(body, new URL(url, location.href).toString());
  if (!manifest) return null;

  return {
    url,
    source: playable.url,
    referer: playable.referer,
    isHLS: true,
    player,
    manifest,
  };
}

/** Décrit ce qu'un flux offre, pour le mode développeur. */
function describe(stream: Stream): string {
  if (!stream.isHLS) return "MP4 progressif";

  const variants = stream.manifest?.variants ?? [];

  if (variants.length === 0) return "playlist de segments";

  return variants.map((variant) => `${variant.height}p`).join(", ");
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

/** Résultat du test d'un lecteur, indépendamment des autres. */
export interface Probe {
  embed: string;
  player: string;
  ok: boolean;
  /** Durée de la résolution complète, en millisecondes. */
  ms: number;
  /** Nombre de flux candidats rendus par le résolveur. */
  playables: number;
  /** Qualités disponibles, ou la raison de l'échec. */
  detail: string;
  /** Hôte amont réellement servi. */
  host: string;
}

/** Teste un lecteur seul : même chemin que la lecture, mais sans rien jouer.
 *  Permet de voir lequel répond, ce qu'il offre et en combien de temps. */
export async function probeEmbed(embed: string): Promise<Probe> {
  const player = embedLabel(embed);
  const started = performance.now();

  const done = (fields: Omit<Probe, "embed" | "player" | "ms">): Probe => ({
    embed,
    player,
    ms: Math.round(performance.now() - started),
    ...fields,
  });

  const playables = await resolvePlayables(embed);

  if (playables.length === 0) {
    return done({ ok: false, playables: 0, detail: "aucun flux extrait", host: "" });
  }

  for (const playable of playables) {
    const stream = await toStream(playable, player);

    if (stream) {
      return done({
        ok: true,
        playables: playables.length,
        detail: describe(stream),
        host: hostOf(stream.source),
      });
    }
  }

  return done({
    ok: false,
    playables: playables.length,
    detail: "flux extrait mais injoignable",
    host: hostOf(playables[0]!.url),
  });
}

/** Teste les lecteurs un par un. Volontairement séquentiel : lancer toutes les
 *  résolutions de front ferait, depuis une seule IP, une rafale que rien dans
 *  une lecture normale ne produit. */
export async function probeEmbeds(
  embeds: string[],
  onProbe?: (probe: Probe, index: number) => void,
): Promise<Probe[]> {
  const probes: Probe[] = [];

  for (const [index, embed] of embeds.entries()) {
    const probe = await probeEmbed(embed);

    probes.push(probe);
    onProbe?.(probe, index);
  }

  return probes;
}

export async function firstPlayableStream(
  embeds: string[],
  onProgress?: ResolveProgress,
): Promise<Stream | null> {
  for (const embed of embeds) {
    const player = embedLabel(embed);

    onProgress?.(`Lecteur ${player}…`);

    for (const playable of await resolvePlayables(embed)) {
      const stream = await toStream(playable, player);
      if (stream) return stream;
    }
  }

  return null;
}
